import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { WarningTwoIcon } from '@chakra-ui/icons'
import { useMsg } from '~/common/localizationUtils'
import { useGetReportsQuery } from '~/api/reports'
import { customReportsApi } from '~/custom_reports/api'
import { REPORT_BY_ID_AND_VERSION } from '~/api/reportsV3'
import { MERGE_NEW_PATH, reportOrdering } from '~/custom_reports/constants'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text
} from '~/design_system'

type SelectedReport = {
  id: number
  name: string
  dataset?: string
}

interface MergeReportsModalProps {
  isOpen: boolean
  onClose: () => void
  selectedReports: SelectedReport[]
  onDeselectReport: (report: SelectedReport) => void
}

function useReportColumnCounts(reports: SelectedReport[]) {
  const results = useQueries({
    queries: reports.map(report => ({
      queryKey: [REPORT_BY_ID_AND_VERSION, { id: report.id, versionId: null }],
      queryFn: () => customReportsApi.reports.get(report.id, null),
      staleTime: Infinity
    }))
  })

  const isLoading = results.some(result => result.isLoading)
  const isError = results.some(result => result.isError)

  const countsById = useMemo(() => {
    const counts: Record<number, number | undefined> = {}
    reports.forEach((report, index) => {
      counts[report.id] = results[index]?.data?.query?.fields?.length
    })
    return counts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, results.map(result => result.dataUpdatedAt).join(',')])

  return { countsById, isLoading, isError }
}

export function MergeReportsModal({ isOpen, onClose, selectedReports, onDeselectReport }: MergeReportsModalProps) {
  const msg = useMsg()
  const router = useRouter()
  const [headerReportId, setHeaderReportId] = useState('')
  const [footerReportId, setFooterReportId] = useState('')

  const { countsById, isLoading, isError } = useReportColumnCounts(selectedReports)

  const { data: { results: allReports } = {} } = useGetReportsQuery({
    ordering: reportOrdering.nameAZ,
    limit: 1000
  })

  // Reference column count = most common count; ties resolve to the first selected report's count.
  const referenceCount = useMemo(() => {
    const present = selectedReports
      .map(report => countsById[report.id])
      .filter((count): count is number => count != null)
    if (present.length === 0) return undefined
    const frequency = new Map<number, number>()
    present.forEach(count => frequency.set(count, (frequency.get(count) ?? 0) + 1))
    const maxFrequency = Math.max(...frequency.values())
    for (const report of selectedReports) {
      const count = countsById[report.id]
      if (count != null && frequency.get(count) === maxFrequency) return count
    }
    return present[0]
  }, [selectedReports, countsById])

  const allCountsResolved = selectedReports.every(report => countsById[report.id] != null)
  const hasMatchingColumns =
    allCountsResolved && new Set(selectedReports.map(report => countsById[report.id])).size === 1
  // A single report passes the column check trivially, and the formatting page it would navigate to
  // has no way to add a source report back — so merging needs both matching columns and 2+ reports.
  const canMerge = selectedReports.length >= 2 && hasMatchingColumns

  const mismatchedReports = selectedReports.filter(
    report => countsById[report.id] != null && countsById[report.id] !== referenceCount
  )

  let alertTitle: string
  let alertDescription: string
  if (canMerge) {
    alertTitle = msg('custom_report.merge.compatible_title', { defaultMessage: 'Reports are compatible' })
    alertDescription = msg('custom_report.merge.compatible_desc', {
      count: selectedReports.length,
      defaultMessage: 'These reports can be merged.'
    })
  } else if (selectedReports.length < 2) {
    alertTitle = msg('custom_report.merge.not_enough_title', { defaultMessage: 'Not enough reports selected' })
    alertDescription = msg('custom_report.merge.not_enough_desc', {
      defaultMessage: 'Select at least 2 reports with the same number of columns to merge.'
    })
  } else {
    alertTitle = msg('custom_report.merge.incompatible_title', { defaultMessage: 'Reports are not compatible' })
    alertDescription = msg('custom_report.merge.incompatible_desc', {
      defaultMessage: "These Reports can't be merged because they do not have the same amount of columns."
    })
  }

  const onContinue = () => {
    const params = new URLSearchParams()
    params.set('sources', selectedReports.map(report => report.id).join(','))
    if (headerReportId) params.set('header', headerReportId)
    if (footerReportId) params.set('footer', footerReportId)
    router.push(`${MERGE_NEW_PATH}?${params.toString()}`)
  }

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center">
          <Text textStyle="h4" m={0}>
            {msg('custom_report.merge.title', { defaultMessage: 'Merge Reports' })}
          </Text>
          <Text textStyle="bodySmall" color="var(--vndly-color-text-muted)" fontWeight="normal" mt={1}>
            {msg('custom_report.merge.subtitle', {
              defaultMessage: 'Combine multiple compatible reports into one, then format the result.'
            })}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Flex justifyContent="center" py={8}>
              <Spinner />
            </Flex>
          ) : (
            <Stack spacing={4}>
              {isError ? (
                <Alert status="error" alignItems="flex-start" borderRadius="md">
                  <AlertIcon />
                  <Text>
                    {msg('custom_report.merge.load_error', {
                      defaultMessage: 'There was an error loading report details. Please try again later.'
                    })}
                  </Text>
                </Alert>
              ) : (
                <Alert status={canMerge ? 'success' : 'error'} alignItems="flex-start" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="semibold" m={0}>
                      {alertTitle}
                    </Text>
                    <Text m={0}>
                      {alertDescription}
                      {mismatchedReports.length > 0 && (
                        <Button
                          variant="inlineLink"
                          ml={1}
                          onClick={() => mismatchedReports.forEach(report => onDeselectReport(report))}>
                          {msg('custom_report.merge.deselect_mismatched', {
                            count: mismatchedReports.length,
                            defaultMessage: `Deselect ${mismatchedReports.length} mismatched`
                          })}
                        </Button>
                      )}
                    </Text>
                  </Box>
                </Alert>
              )}

              <Box>
                <Text textStyle="bodySmallSemi" mb={2}>
                  {msg('custom_report.merge.selected_reports', {
                    count: selectedReports.length,
                    defaultMessage: `Selected Reports (${selectedReports.length})`
                  })}
                </Text>
                <Stack spacing={3}>
                  {selectedReports.map(report => {
                    const count = countsById[report.id]
                    const isMismatch = count != null && count !== referenceCount
                    return (
                      <Flex key={report.id} justifyContent="space-between" alignItems="center">
                        <Box minW={0}>
                          <Text textStyle="bodySemi" m={0} noOfLines={1}>
                            {report.name}
                          </Text>
                          {report.dataset && (
                            <Text textStyle="bodySmall" color="var(--vndly-color-text-muted)" m={0} noOfLines={1}>
                              {report.dataset}
                            </Text>
                          )}
                        </Box>
                        <Flex alignItems="center" gap={3} flexShrink={0} ml={3}>
                          <Flex
                            alignItems="center"
                            gap={1}
                            color={isMismatch ? 'var(--vndly-color-text-error)' : undefined}>
                            <Text textStyle="bodySmall" m={0}>
                              {msg('custom_report.merge.columns', {
                                count: count ?? 0,
                                defaultMessage: `${count ?? 0} columns`
                              })}
                            </Text>
                            {isMismatch && <WarningTwoIcon boxSize={3} />}
                          </Flex>
                        </Flex>
                      </Flex>
                    )
                  })}
                </Stack>
              </Box>

              <FormControl>
                <FormLabel>{msg('custom_report.merge.header_report', { defaultMessage: 'Header Report' })}</FormLabel>
                <Select value={headerReportId} onChange={event => setHeaderReportId(event.target.value)}>
                  <option value="">{msg('custom_report.merge.none', { defaultMessage: 'None' })}</option>
                  {allReports?.map(report => (
                    <option key={report.id} value={report.id}>
                      {report.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText>
                  {msg('custom_report.merge.header_help', {
                    defaultMessage: 'Rows from this report render at the top of the merged file.'
                  })}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>{msg('custom_report.merge.footer_report', { defaultMessage: 'Footer Report' })}</FormLabel>
                <Select value={footerReportId} onChange={event => setFooterReportId(event.target.value)}>
                  <option value="">{msg('custom_report.merge.none', { defaultMessage: 'None' })}</option>
                  {allReports?.map(report => (
                    <option key={report.id} value={report.id}>
                      {report.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText>
                  {msg('custom_report.merge.footer_help', {
                    defaultMessage: 'Rows from this report render at the bottom of the merged file.'
                  })}
                </FormHelperText>
              </FormControl>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter justifyContent="flex-end" gap={3}>
          <Button onClick={onClose}>{msg('button.cancel', { defaultMessage: 'Cancel' })}</Button>
          <Button variant="primary" isDisabled={!canMerge} onClick={onContinue}>
            {msg('custom_report.merge.continue', { defaultMessage: 'Continue to Formatting' })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
